import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ChapterTOC } from '@/components/ChapterTOC';
import { FeedbackButtons } from '@/features/reader/FeedbackButtons';
import { useReaderStore } from '@/features/reader/readerStore';
import { isGenerating, type NodeData, useNode } from '@/features/reader/useNodes';
import { useInvalidateReadingPath, useReadingPath } from '@/features/reader/useReadingPath';
import { useStory } from '@/features/stories/useStories';
import { useReaderTheme } from '@/hooks/useReaderTheme';
import {
  trackBranchExplored,
  trackChapterFinished,
  trackChapterStarted,
  trackChoiceMade,
  trackGenerationWaitEnded,
  trackGenerationWaitStarted,
  trackNodeError,
  trackStorySelected,
} from '@/services/analytics';

export default function ReaderPage() {
  const { storyId, nodeId: urlNodeId } = useParams<{ storyId: string; nodeId?: string }>();
  const navigate = useNavigate();
  const { theme, fontSize } = useReaderTheme();
  const { data: story, isLoading: storyLoading } = useStory(storyId);
  const {
    currentStoryId,
    currentNodeId,
    startReading,
    navigateToNode,
    goBack,
    historyIndex,
    chapterPath,
    setChapterPath,
    jumpToChapter,
  } = useReaderStore();
  const invalidateReadingPath = useInvalidateReadingPath();

  // Track selection with the nodeId it was made on, so it auto-resets when navigating
  const [selection, setSelection] = useState<{ nodeId: string; choice: string } | null>(null);
  const selectedChoice = selection?.nodeId === currentNodeId ? selection.choice : null;

  // ToC drawer state
  const [isTocOpen, setIsTocOpen] = useState(false);

  // Analytics tracking state
  const chapterStartTime = useRef<number | null>(null);
  const hasTrackedChapterStart = useRef<string | null>(null);
  const hasTrackedChapterFinish = useRef<string | null>(null);
  const generationStartTime = useRef<number | null>(null);
  const previousChoices = useRef<Map<string, number>>(new Map()); // nodeId -> choiceIndex
  const hasTrackedStorySelection = useRef<string | null>(null);
  // Track access status for current chapter (free/explored/locked) - determined when navigating
  const currentChapterAccessStatus = useRef<'free' | 'explored' | 'locked'>('free');
  const hasTrackedNodeError = useRef<string | null>(null);
  // Track scroll-to-end and time conditions separately for completion tracking
  const hasScrolledToEnd = useRef(false);

  // Fetch reading path from server
  const { data: readingPath, isFetched: isReadingPathFetched } = useReadingPath(storyId);

  // Start reading when both story and reading path query complete
  // Wait for readingPath query to settle to avoid race condition
  useEffect(() => {
    if (story && storyId && isReadingPathFetched) {
      const storeStoryId = useReaderStore.getState().currentStoryId;

      // Only initialize if we haven't already started this story
      if (storeStoryId !== storyId) {
        // If server has progress, resume from there
        if (readingPath?.currentNodeId && readingPath.path.length > 0) {
          startReading(storyId, readingPath.currentNodeId);
          setChapterPath(readingPath.path);
          // Set up nodeHistory from path, keeping historyIndex and currentNodeId
          // in sync. startReading may have restored stale local progress, so we
          // explicitly align all three fields to the server reading path.
          const pathNodeIds = readingPath.path.map((c) => c.nodeId);
          const currentIndex = pathNodeIds.indexOf(readingPath.currentNodeId);
          const resolvedIndex = currentIndex >= 0 ? currentIndex : pathNodeIds.length - 1;
          useReaderStore.setState({
            currentNodeId: pathNodeIds[resolvedIndex],
            nodeHistory: pathNodeIds,
            historyIndex: resolvedIndex,
          });
        } else {
          // No server progress - start fresh from root
          startReading(storyId, story.rootNodeId);
        }
      }

      if (urlNodeId) {
        // URL specifies a node — sync store to match (handles refresh)
        const state = useReaderStore.getState();
        if (state.currentNodeId !== urlNodeId) {
          const indexInHistory = state.nodeHistory.indexOf(urlNodeId);

          if (indexInHistory >= 0) {
            // Node already in history: just move the pointer
            useReaderStore.setState({
              currentNodeId: urlNodeId,
              historyIndex: indexInHistory,
            });
          } else if (readingPath?.path && readingPath.path.length > 0) {
            // Node not in history: rebuild from reading path
            const pathNodeIds = readingPath.path.map((c) => c.nodeId);
            const indexInPath = pathNodeIds.indexOf(urlNodeId);

            if (indexInPath >= 0) {
              useReaderStore.setState({
                currentNodeId: urlNodeId,
                nodeHistory: pathNodeIds,
                historyIndex: indexInPath,
              });
            } else {
              // URL node isn't in server path; minimal consistent history
              useReaderStore.setState({
                currentNodeId: urlNodeId,
                nodeHistory: [urlNodeId],
                historyIndex: 0,
              });
            }
          } else {
            // No reading path available; minimal consistent history
            useReaderStore.setState({
              currentNodeId: urlNodeId,
              nodeHistory: [urlNodeId],
              historyIndex: 0,
            });
          }
        }
      } else {
        // No nodeId in URL — redirect to the current position
        const resolvedNodeId = useReaderStore.getState().currentNodeId;
        if (resolvedNodeId) {
          navigate(`/story/${storyId}/${resolvedNodeId}`, { replace: true });
        }
      }
    }
  }, [
    story,
    storyId,
    readingPath,
    isReadingPathFetched,
    startReading,
    setChapterPath,
    urlNodeId,
    navigate,
  ]);

  // Track story selection when entering reader
  useEffect(() => {
    if (story && storyId && hasTrackedStorySelection.current !== storyId) {
      hasTrackedStorySelection.current = storyId;
      trackStorySelected(storyId, story.title);
    }
  }, [story, storyId]);

  // Update chapterPath when server path changes
  useEffect(() => {
    if (readingPath?.path) {
      setChapterPath(readingPath.path);
    }
  }, [readingPath, setChapterPath]);

  // Only fetch the node when the store has initialized for this story.
  // During story transitions, currentNodeId may still point to a node from the
  // previous story, which would cause a brief "Wrong Story" flash.
  const isStoreReady = currentStoryId === storyId;
  const {
    data: nodeData,
    isLoading: nodeLoading,
    isError: nodeError,
    error: nodeErrorDetails,
  } = useNode(isStoreReady ? (currentNodeId ?? undefined) : undefined);
  // Note: Polling for generating nodes is handled by refetchInterval in useNode

  // Track chapter started when node loads
  useEffect(() => {
    if (nodeData && !isGenerating(nodeData) && storyId && currentNodeId) {
      const node = nodeData as NodeData;
      // Only track if we haven't tracked this node yet
      if (hasTrackedChapterStart.current !== currentNodeId) {
        hasTrackedChapterStart.current = currentNodeId;
        chapterStartTime.current = Date.now();
        hasTrackedChapterFinish.current = null; // Reset finish tracking for new chapter

        // Use tracked access status (set when navigating to this chapter)
        const isFree =
          currentChapterAccessStatus.current === 'free' ||
          currentChapterAccessStatus.current === 'explored';
        hasScrolledToEnd.current = false; // Reset scroll tracking for new chapter
        trackChapterStarted(storyId, currentNodeId, node.chapterNumber, isFree, node.isEnding);
      }
    }
  }, [nodeData, storyId, currentNodeId]);

  // Track generation wait times
  useEffect(() => {
    if (nodeData && storyId && currentNodeId) {
      if (isGenerating(nodeData)) {
        // Generation started
        if (generationStartTime.current === null) {
          generationStartTime.current = Date.now();
          trackGenerationWaitStarted(storyId, currentNodeId);
        }
      } else {
        // Generation ended
        if (generationStartTime.current !== null) {
          const waitSeconds = (Date.now() - generationStartTime.current) / 1000;
          trackGenerationWaitEnded(storyId, currentNodeId, waitSeconds);
          generationStartTime.current = null;
        }
      }
    }
  }, [nodeData, storyId, currentNodeId]);

  // Track node error in analytics (once per node)
  useEffect(() => {
    if (!nodeError || !storyId || !currentNodeId) return;
    if (hasTrackedNodeError.current === currentNodeId) return;

    hasTrackedNodeError.current = currentNodeId;
    const errorResponse = nodeErrorDetails as {
      response?: { status: number; data?: { error?: { code?: string } } };
    };
    const status = errorResponse?.response?.status;
    const errorType =
      status === 422 ? 'generation_failed' : status === 404 ? 'not_found' : 'unknown';
    trackNodeError(storyId, currentNodeId, errorType, status);
  }, [nodeError, nodeErrorDetails, storyId, currentNodeId]);

  // Helper to check and track chapter completion
  const checkAndTrackCompletion = useCallback(() => {
    if (!storyId || !currentNodeId || !nodeData || isGenerating(nodeData)) return;
    if (hasTrackedChapterFinish.current === currentNodeId) return;

    const node = nodeData as NodeData;
    const minTimeSeconds = 30; // Minimum 30 seconds to count as "read"

    const timeSpentSeconds = chapterStartTime.current
      ? (Date.now() - chapterStartTime.current) / 1000
      : 0;
    const hasSpentEnoughTime = timeSpentSeconds >= minTimeSeconds;

    if (hasScrolledToEnd.current && hasSpentEnoughTime) {
      hasTrackedChapterFinish.current = currentNodeId;
      const isFree =
        currentChapterAccessStatus.current === 'free' ||
        currentChapterAccessStatus.current === 'explored';
      trackChapterFinished(
        storyId,
        currentNodeId,
        node.chapterNumber,
        Math.round(timeSpentSeconds),
        isFree,
        node.isEnding
      );
    }
  }, [storyId, currentNodeId, nodeData]);

  // Track chapter finished on scroll to end + time threshold
  const handleScroll = useCallback(() => {
    if (!storyId || !currentNodeId || !nodeData || isGenerating(nodeData)) return;
    if (hasTrackedChapterFinish.current === currentNodeId) return;

    const scrollPosition = window.scrollY + window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollThreshold = 0.9; // 90% scrolled

    if (scrollPosition / documentHeight >= scrollThreshold) {
      hasScrolledToEnd.current = true;
      checkAndTrackCompletion();
    }
  }, [storyId, currentNodeId, nodeData, checkAndTrackCompletion]);

  // Add scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Timer-based completion check (handles case where user scrolls to end quickly, then waits)
  useEffect(() => {
    if (!storyId || !currentNodeId || !nodeData || isGenerating(nodeData)) return;
    if (hasTrackedChapterFinish.current === currentNodeId) return;

    const minTimeSeconds = 30;
    const timer = setInterval(() => {
      if (hasScrolledToEnd.current) {
        checkAndTrackCompletion();
      }
    }, 1000); // Check every second once scrolled to end

    // Also set a timeout for when min time is reached
    const timeout = setTimeout(() => {
      checkAndTrackCompletion();
    }, minTimeSeconds * 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(timeout);
    };
  }, [storyId, currentNodeId, nodeData, checkAndTrackCompletion]);

  // Handle choice selection - navigate to child node by index
  const handleChoiceClick = (choiceIndex: 0 | 1 | 2) => {
    const node = nodeData as NodeData;
    const choiceKey = String(choiceIndex) as '0' | '1' | '2';
    const childNodeId = node.childNodeIds[choiceKey];
    if (childNodeId && currentNodeId && storyId) {
      // Track choice analytics
      const previousChoice = previousChoices.current.get(currentNodeId);
      const isNewBranch = previousChoice !== undefined && previousChoice !== choiceIndex;

      // Track Branch Explored if user is picking a different choice than before
      if (isNewBranch) {
        trackBranchExplored(
          storyId,
          currentNodeId,
          node.chapterNumber,
          previousChoice,
          choiceIndex
        );
      }

      // Track Choice Made
      trackChoiceMade(storyId, currentNodeId, node.chapterNumber, choiceIndex, isNewBranch);

      // Store this choice for future branch detection
      previousChoices.current.set(currentNodeId, choiceIndex);

      // Track access status for the next chapter (used in analytics)
      const accessStatus = node.childAccess?.[choiceKey] ?? 'locked';
      currentChapterAccessStatus.current =
        accessStatus === 'free' || accessStatus === 'explored' || accessStatus === 'locked'
          ? accessStatus
          : 'locked';

      setSelection({ nodeId: currentNodeId, choice: String(choiceIndex) });
      navigateToNode(childNodeId);
      navigate(`/story/${storyId}/${childNodeId}`);
      // Invalidate reading path after navigation to get updated path from server
      setTimeout(() => {
        invalidateReadingPath(storyId);
      }, 500);
    }
  };

  // Handle chapter selection from ToC
  const handleSelectChapter = (nodeId: string, index: number) => {
    setSelection(null);
    jumpToChapter(index);
    if (storyId) {
      navigate(`/story/${storyId}/${nodeId}`);
      // Invalidate path after jumping
      setTimeout(() => {
        invalidateReadingPath(storyId);
      }, 500);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    setSelection(null);
    const previousNodeId = goBack();
    if (previousNodeId && storyId) {
      navigate(`/story/${storyId}/${previousNodeId}`);
    }
  };

  if (storyLoading || !story) {
    return (
      <div
        className="min-h-screen bg-reader-bg flex items-center justify-center"
        data-reader-theme={theme}
        data-reader-font={fontSize}
      >
        <div className="animate-pulse text-reader-text-secondary text-xl">Loading story...</div>
      </div>
    );
  }

  // Handle node fetch error (e.g., generation failed, not found)
  if (nodeError) {
    const errorResponse = nodeErrorDetails as {
      response?: { status: number; data?: { error?: { code?: string } } };
    };
    const status = errorResponse?.response?.status;
    const isGenerationFailed = status === 422;
    const isNotFound = status === 404;

    let title = 'Something Went Wrong';
    let message =
      "We couldn't load this part of the story right now. Please go back and try again later.";

    if (isGenerationFailed) {
      title = 'Story Unavailable';
      message =
        "Our storytellers couldn't finish this chapter. Please go back and try again later.";
    } else if (isNotFound) {
      title = 'Story Unavailable';
      message = "This chapter isn't available yet. Please go back and try again later.";
    }

    return (
      <div
        className="min-h-screen bg-reader-bg flex items-center justify-center"
        data-reader-theme={theme}
        data-reader-font={fontSize}
      >
        <div className="text-center max-w-md px-4">
          <div className="text-reader-error text-6xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-reader-text-heading mb-2">{title}</h2>
          <p className="text-reader-text-secondary mb-6">{message}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {historyIndex > 0 && (
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-reader-accent hover:bg-reader-accent-hover text-reader-accent-text rounded-lg font-medium transition-colors"
              >
                Go Back
              </button>
            )}
            <Link
              to="/"
              className="px-6 py-3 border border-reader-accent text-reader-text-secondary hover:bg-reader-accent-bg rounded-lg font-medium transition-colors"
            >
              Return to Library
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (nodeLoading || !nodeData) {
    return (
      <div
        className="min-h-screen bg-reader-bg flex items-center justify-center"
        data-reader-theme={theme}
        data-reader-font={fontSize}
      >
        <div className="animate-pulse text-reader-text-secondary text-xl">Loading chapter...</div>
      </div>
    );
  }

  // Show generating state
  if (isGenerating(nodeData)) {
    return (
      <div
        className="min-h-screen bg-reader-bg flex items-center justify-center"
        data-reader-theme={theme}
        data-reader-font={fontSize}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-reader-accent mx-auto mb-4" />
          <p className="text-reader-text-secondary text-xl">Generating your story...</p>
          <p className="text-reader-text-muted mt-2 text-sm">This may take a moment</p>
        </div>
      </div>
    );
  }

  const node = nodeData as NodeData;

  // Validate URL nodeId belongs to the correct story (prevent cross-story mismatch)
  if (storyId && node.storyId !== storyId) {
    return (
      <div
        className="min-h-screen bg-reader-bg flex items-center justify-center"
        data-reader-theme={theme}
        data-reader-font={fontSize}
      >
        <div className="text-center max-w-md px-4">
          <div className="text-reader-error text-6xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-reader-text-heading mb-2">Wrong Story</h2>
          <p className="text-reader-text-secondary mb-6">
            This chapter belongs to a different story. Redirecting to the library.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-reader-accent hover:bg-reader-accent-hover text-reader-accent-text rounded-lg font-medium transition-colors"
          >
            Return to Library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-reader-bg"
      data-reader-theme={theme}
      data-reader-font={fontSize}
    >
      {/* ToC Drawer */}
      <ChapterTOC
        isOpen={isTocOpen}
        onClose={() => setIsTocOpen(false)}
        chapters={chapterPath}
        currentNodeId={currentNodeId}
        onSelectChapter={handleSelectChapter}
      />

      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-reader-border bg-reader-bg-surface backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => setIsTocOpen(true)}
            className="text-reader-text-secondary hover:text-reader-text-heading transition-colors p-1"
            aria-label="Open chapter list"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div className="text-center">
            <h1 className="text-lg font-semibold text-reader-text-heading truncate max-w-[200px] md:max-w-none">
              {story.title}
            </h1>
            <p className="text-xs text-reader-text-muted">
              {node.chapterTitle
                ? `Chapter ${node.chapterNumber}: ${node.chapterTitle}`
                : `Chapter ${node.chapterNumber}`}
            </p>
          </div>
          <Link
            to="/"
            className="text-reader-text-secondary hover:text-reader-text-heading transition-colors p-1"
            aria-label="Exit to library"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Link>
        </div>
      </header>

      {/* Reading progress */}
      {historyIndex > 0 && (
        <div className="max-w-3xl mx-auto px-4 py-2">
          <div className="flex items-center gap-2 text-xs text-reader-text-muted">
            <button
              onClick={handleBack}
              className="text-reader-text-secondary hover:text-reader-text-heading transition-colors underline"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Story content */}
        <article className="max-w-none">
          <div
            className="text-reader-text whitespace-pre-wrap"
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'var(--reader-font-size)',
              lineHeight: 'var(--reader-line-height)',
            }}
          >
            {node.text}
          </div>
        </article>

        {/* Feedback buttons */}
        {currentNodeId && <FeedbackButtons nodeId={currentNodeId} />}

        {/* Choices or ending */}
        <div className="mt-10 pt-8 border-t border-reader-border">
          {node.isEnding ? (
            <div className="text-center">
              <h3 className="text-2xl font-bold text-reader-text-heading mb-4">The End</h3>
              <p className="text-reader-text-secondary mb-6">Thank you for reading!</p>
              <Link
                to="/"
                className="inline-block px-6 py-3 bg-reader-accent hover:bg-reader-accent-hover text-reader-accent-text rounded-lg font-medium transition-colors"
              >
                Read Another Story
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-reader-text-secondary">
                  What do you do?
                </h3>
                {node.tokenBalance !== undefined && (
                  <span className="text-sm text-reader-token flex items-center gap-1">
                    <span>&#x1FA99;</span> {node.tokenBalance} tokens
                  </span>
                )}
              </div>
              {node.choices.map((choice, index) => {
                const choiceIndex = index as 0 | 1 | 2;
                const choiceKey = String(choiceIndex) as '0' | '1' | '2';
                const childNodeId = node.childNodeIds[choiceKey];
                const isSelected = selectedChoice === String(choiceIndex);
                const accessStatus = node.childAccess?.[choiceKey] ?? 'locked';
                const isExplored = accessStatus === 'explored';
                const isFree = accessStatus === 'free';
                const isLocked = accessStatus === 'locked';
                const isInsufficient = accessStatus === 'insufficient';
                const isUnavailable = accessStatus === 'unavailable';
                const isDisabled = isInsufficient || isUnavailable;

                return (
                  <button
                    key={index}
                    onClick={() => handleChoiceClick(choiceIndex)}
                    disabled={selectedChoice !== null || !childNodeId || isDisabled}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isSelected
                        ? 'border-reader-accent bg-reader-accent-bg'
                        : isDisabled
                          ? 'border-reader-border bg-reader-bg-card opacity-50 cursor-not-allowed'
                          : selectedChoice !== null
                            ? 'border-reader-border bg-reader-bg-card opacity-50 cursor-not-allowed'
                            : isExplored
                              ? 'border-reader-choice-explored-border bg-reader-choice-explored-bg hover:border-reader-choice-explored-border/75 hover:bg-reader-choice-explored-bg/75'
                              : isFree
                                ? 'border-reader-status-free/30 bg-reader-status-free/5 hover:border-reader-status-free/50 hover:bg-reader-status-free/10'
                                : 'border-reader-status-locked/30 bg-reader-status-locked/5 hover:border-reader-status-locked/50 hover:bg-reader-status-locked/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isSelected
                            ? 'bg-reader-accent-bg text-reader-text-secondary'
                            : isDisabled
                              ? 'bg-reader-bg-card text-reader-text-muted'
                              : isExplored
                                ? 'bg-reader-badge-explored-bg text-reader-accent-text'
                                : isFree
                                  ? 'bg-reader-badge-free-bg text-reader-accent-text'
                                  : 'bg-reader-badge-locked-bg text-reader-accent-text'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p
                          className={
                            isSelected
                              ? 'text-reader-text-heading font-medium'
                              : isDisabled
                                ? 'text-reader-text-muted font-medium'
                                : 'text-reader-text-heading font-medium'
                          }
                        >
                          {choice.text}
                        </p>
                        {choice.consequenceHint && (
                          <p className="text-sm mt-1 italic text-reader-text-muted">
                            {choice.consequenceHint}
                          </p>
                        )}
                        {!isSelected && (
                          <p
                            className={`text-xs mt-2 uppercase tracking-wide flex items-center gap-1 ${
                              isUnavailable
                                ? 'text-reader-text-muted'
                                : isInsufficient
                                  ? 'text-reader-status-insufficient'
                                  : isExplored
                                    ? 'text-reader-status-explored'
                                    : isFree
                                      ? 'text-reader-status-free'
                                      : 'text-reader-status-locked'
                            }`}
                          >
                            {isExplored && 'Previously explored'}
                            {isFree && (
                              <>
                                <span className="text-base">&#x2728;</span> Free path
                              </>
                            )}
                            {isLocked && (
                              <>
                                <span className="text-base">&#x1FA99;</span> 1 token
                              </>
                            )}
                            {isInsufficient && (
                              <>
                                <span className="text-base">&#x1F512;</span> Insufficient tokens
                              </>
                            )}
                            {isUnavailable && 'Not available yet'}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
